<template>
  <div>
    <b-form-group label="1. Client Information">
      <p class="my-1">
        <span class="font-weight-bold">Legal Name: </span
        >{{ patient?.patient | formatClient }}
      </p>
      <p class="my-1">
        <span class="font-weight-bold">DOB: </span
        >{{ formatDateOnly(patient?.patient?.dateOfBirth) }}
      </p>
    </b-form-group>
    <b-form-group
      label="2. I give my permission to the clinic and professional(s) stated below"
    >
      <p class="my-0">{{ systemSettings.organizationName }}</p>
      <p class="my-0">
        {{ getOrganizationInfo }}
      </p>
    </b-form-group>
    <b-form-group label="Attention (optional):">
      <b-input class="mt-2 mb-0" v-model="attention" />
      <small class=""
        >Specify name(s) of provider(s) you are working with</small
      >
    </b-form-group>
    <b-form-group
      label="3. To:"
      :state="errors.releaseTo.length == 0 ? null : false"
      :invalid-feedback="errors.releaseTo.join(', ')"
    >
      <b-form-radio-group
        name="release-to"
        v-model="releaseTo"
        :options="releaseToOptions"
        stacked
      />
    </b-form-group>
    <b-form-group
      label="Entity"
      class="mt-3"
      :state="errors.entity.length == 0 ? null : false"
      :invalid-feedback="errors.entity.join(', ')"
    >
      <b-form-radio-group
        name="entity"
        v-model="entity"
        :options="entityOptions"
        plain
      />
    </b-form-group>
    <b-form-group v-show="entity == 0" label="Contact" class="mt-3">
      <v-select
        v-model="contact"
        :options="releaseToContactOptions"
        @input="getContactData()"
      />
    </b-form-group>
    <b-form-group
      v-show="entity == 0"
      label="Contact Information"
      class="mt-3"
    >
      <b-row class="mb-3">
        <b-col>
          <small>Full Name <span class="text-red">*</span></small>
          <b-form-input
            v-model="contactFullName"
            :state="errors.contact.name.length == 0 ? null : false"
            aria-describedby="input-full-name-error"
          />
          <b-form-invalid-feedback id="input-full-name-error" class="text-red">
            {{ errors.contact.name.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col>
          <small>Relationship</small>
          <v-select
            v-model="contactRelationship"
            :options="relationships"
            :disabled="contact?.value != 0"
          />
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col>
          <small>Email</small>
          <b-form-input
            v-model="contactEmail"
            :state="errors.contact.email.length == 0 ? null : false"
            aria-describedby="input-email-error"
          />
          <b-form-invalid-feedback id="input-email-error" class="text-red">
            {{ errors.contact.email.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col>
          <small>Mobile Phone Number</small>
          <phone-input
            ref="contactPhoneInput"
            defaultCountry="US"
            v-model="contactPhone"
            trim
            :state="errors.contact.phone.length == 0 ? null : false"
            aria-describedby="input-phone-error"
          ></phone-input>
          <b-form-invalid-feedback id="input-phone-error" class="text-red">
            {{ errors.contact.phone.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col>
          <small>Street Address</small>
          <b-form-input v-model="contactStreet" />
        </b-col>
        <b-col>
          <small>City</small>
          <b-form-input v-model="contactCity" />
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col>
          <small>Zip Code</small>
          <b-form-input v-model="contactZipcode" v-facade="'#####'" trim />
        </b-col>
        <b-col>
          <small>State</small>
          <v-select
            v-model="contactState"
            :options="states"
            :disabled="contact?.value != 0"
          />
        </b-col>
      </b-row>
    </b-form-group>
    <b-form-group
      v-show="entity == 1"
      class="mt-3"
    >
      <b-row class="mb-3">
        <b-col>
          <small>Organization/Agency Name</small>
          <b-form-input
            v-model="otherName"
            :state="errors.other.name.length == 0 ? null : false"
            aria-describedby="input-other-name-error"
          />
          <b-form-invalid-feedback id="input-other-name-error" class="text-red">
            {{ errors.other.name.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col>
          <small>Attention (Optional)</small>
          <b-form-input v-model="otherAttention" />
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col>
          <small>Email</small>
          <b-form-input
            v-model="otherEmail"
            :state="errors.other.email.length == 0 ? null : false"
            aria-describedby="input-other-email-error"
          />
          <b-form-invalid-feedback id="input-other-email-error" class="text-red">
            {{ errors.other.email.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col>
          <small>Phone</small>
          <phone-input
            ref="otherPhoneInput"
            defaultCountry="US"
            v-model="otherPhone"
            trim
            :state="errors.other.phone.length == 0 ? null : false"
            aria-describedby="input-other-phone-error"
          ></phone-input>
          <b-form-invalid-feedback id="input-other-phone-error" class="text-red">
            {{ errors.other.phone.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col>
          <small>Address</small>
          <b-form-input
            v-model="otherAddress"
            :state="errors.other.address.length == 0 ? null : false"
            aria-describedby="input-other-address-error"
          />
          <b-form-invalid-feedback id="input-other-address-error" class="text-red">
            {{ errors.other.address.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col>
          <small>Fax</small>
          <phone-input
            ref="contactPhoneInput"
            defaultCountry="US"
            v-model="otherFax"
            trim
            :state="errors.other.fax.length == 0 ? null : false"
            aria-describedby="input-other-fax-error"
          ></phone-input>
          <b-form-invalid-feedback id="input-other-fax-error" class="text-red">
            {{ errors.other.fax.join(", ") }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
    </b-form-group>
    <b-form-group
      label="4. Expiration Date*"
      :state="errors.expirationDate.length == 0 ? null : false"
      :invalid-feedback="errors.expirationDate.join(', ')"
    >
      <date-picker v-model="expirationDate" :errors="errors.expirationDate" />
    </b-form-group>
  </div>
</template>
<script>
import { mapState } from "vuex";
import systemSettingService from "../../../server/systemSetting";
import eocService from "../../../server/eoc";
import patientContactsServer from "../../../server/patientContacts";
import releaseOfInformationServer from "../../../server/releaseOfInformation";
import DatePicker from "../../controls/DatePicker";
import PhoneInput from "../../controls/PhoneInput.vue";
import {
  validateEmail,
  validatePhoneNumber
} from "../../../helpers/validation";

export default {
  name: "DocumentReleaseOfInformation",
  components: {
    DatePicker,
    PhoneInput
  },
  props: ["documentId", "documentStatus", "effectiveDateTime", "eocId"],
  data() {
    return {
      errors: {
        releaseTo: [],
        entity: [],
        expirationDate: [],
        contact: {
          name: [],
          phone: [],
          email: []
        },
        other: {
          name: [],
          phone: [],
          email: [],
          address: [],
          fax: []
        },
      },
      releaseTo: null,
      entity: null,
      releaseToOptions: [
        { value: 0, text: "Obtain Information From" },
        { value: 1, text: "Disclose Information To" },
        { value: 2, text: "Exchange Information With" }
      ],
      entityOptions: [
        { value: 0, text: "Contact" },
        { value: 1, text: "Other" }
      ],
      contact: null,
      attention: null,
      releaseToContactOptions: [],
      contactFullName: null,
      contactRelationship: null,
      contactEmail: null,
      contactPhone: null,
      contactStreet: null,
      contactCity: null,
      contactZipcode: null,
      contactState: null,
      otherName: null,
      otherAttention: null,
      otherEmail: null,
      otherPhone: null,
      otherAddress: null,
      otherFax: null,
      systemSettings: null,
      patient: null,
      formId: null,
      expirationDate: null,
      contacts: null
    };
  },
  async mounted() {
    this.systemSettings = await systemSettingService.get();
    this.patient = await eocService.get(this.eocId);
    await this.fetchReleaseToContactOptions();
    await this.fecthReleaseOfInformation();
  },
  methods: {
    async fetchReleaseToContactOptions() {
      this.releaseToContactOptions = [];
      const contacts = await patientContactsServer.get(this.patient.patientId);
      this.contacts = contacts;
      this.releaseToContactOptions = this.contacts.map(x => ({
        value: x.id,
        label: `${x.name} ${
          x.relationship ? "(" + x.relationship.name + ")" : ""
        }`,
        data: x
      }));
      this.releaseToContactOptions.unshift({ value: 0, label: "New Contact" });
      this.contact = this.releaseToContactOptions[0];
    },
    async fecthReleaseOfInformation() {
      if (this.documentId) {
        const releaseOfInformation = await releaseOfInformationServer.getFromDocument(
          this.documentId
        );
        if (releaseOfInformation != null) {
          this.formId = releaseOfInformation.id;
          this.releaseTo =
            releaseOfInformation.toType == null
              ? null
              : releaseOfInformation.toType == "ObtainInformationFrom"
              ? 0
              : releaseOfInformation.toType == "DiscloseInformationTo"
              ? 1
              : 2;
          this.entity =
            releaseOfInformation.toEntity == null
              ? null
              : releaseOfInformation.toEntity == "Contact"
              ? 0
              : 1;

          if (releaseOfInformation.toPatientContact != null) {
            this.contact = this.releaseToContactOptions.find(
              x => x.value == releaseOfInformation.toPatientContact.id
            );
            const contactInfo = releaseOfInformation.toPatientContact;
            this.contactFullName = contactInfo.name ?? "";
            this.contactRelationship = contactInfo.relationship
              ? this.relationships.find(
                  x => x.value == contactInfo.relationship.id
                )
              : "";
            this.contactEmail = contactInfo.email ?? "";
            this.contactPhone = contactInfo.phone ?? "";
            this.contactStreet = contactInfo.address ?? "";
            this.contactCity = contactInfo.city ?? "";
            this.contactZipcode = contactInfo.zipCode ?? "";
            this.contactState = contactInfo.state
              ? this.states.find(x => x.value == contactInfo.state.id)
              : "";
          } else {
            this.otherName = releaseOfInformation.toOrganizationName ?? "";
            this.otherAttention =
              releaseOfInformation.toOrganizationAttention ?? "";
            this.otherEmail = releaseOfInformation.toOrganizationEmail ?? "";
            this.otherPhone = releaseOfInformation.toOrganizationPhone ?? "";
            this.otherAddress =
              releaseOfInformation.toOrganizationAddress ?? "";
            this.otherFax = releaseOfInformation.toOrganizationFax ?? "";
          }
          this.attention = releaseOfInformation.attention ?? "";
          this.expirationDate = this.formatDateOnly(
            releaseOfInformation.expirationDate
          );
        }
      } else {
        this.expirationDate = new Date(this.effectiveDateTime);
        this.expirationDate.setFullYear(this.expirationDate.getFullYear() + 1);
      }
    },
    cleanContactData() {
      this.contactFullName = null;
      this.contactRelationship = null;
      this.contactEmail = null;
      this.contactPhone = null;
      this.contactStreet = null;
      this.contactCity = null;
      this.contactZipcode = null;
      this.contactState = null;
    },
    getContactData() {
      if (this.contact.value != 0) {
        const contactData = this.contact.data;
        this.cleanContactData();
        this.contactFullName = contactData.name;
        this.contactRelationship = contactData.relationship
          ? this.relationships.find(x => x.value == contactData.relationship.id)
          : "";
        this.contactEmail = contactData.email ?? null;
        this.contactPhone = contactData.phone ?? null;
        this.contactStreet = contactData.address ?? null;
        this.contactCity = contactData.city ?? null;
        this.contactZipcode = contactData.zipCode ?? null;
        this.contactState = contactData.state
          ? this.states.find(x => x.value == contactData.state.id)
          : "";
      }
    },
    isEmailContact(email) {
      const contactEmails = this.contacts.map(contact => contact.email);
      return contactEmails.indexOf(email) >= 0;
    },
    isPhoneContact(phone) {
      const contactPhones = this.contacts.map(contact => contact.phone);
      return contactPhones.indexOf(phone) >= 0;
    },
    validateReleaseOfInformationSubmit() {
      let withoutErrors = true;
      this.resetErrors();
      if (this.documentStatus != "Draft") {
        if (this.releaseTo == null) {
          this.errors.releaseTo.push("Required field!");
          withoutErrors = false;
        }
        if (this.entity == null) {
          this.errors.entity.push("Required field!");
          withoutErrors = false;
        }
        if (this.expirationDate == null) {
          this.errors.expirationDate.push("Required field!");
          withoutErrors = false;
        }
        if (this.entity == 0) {
          if (this.contact.value == 0) {
            if (this.contactFullName == null || this.contactFullName == "") {
              this.errors.contact.name.push("Required Full Name field!");
              withoutErrors = false;
            }
            if (this.contactEmail) {
              if (validateEmail(this.contactEmail)) {
                if (this.isEmailContact(this.contactEmail) || this.patient.patient.email === this.contactEmail) {
                  this.errors.contact.email.push(
                    'This email address is being used by another contact or the patient. Choose "Other" under "Entity" rather than creating a new contact.'
                  );
                  withoutErrors = false;
                }
              } else {
                this.errors.contact.email.push("Invalid email");
                withoutErrors = false;
              }
            }
            if (this.contactPhone) {
              if (validatePhoneNumber(this.contactPhone)) {
                if (this.isPhoneContact(this.contactPhone)) {
                  this.errors.contact.phone.push(
                    "This phone is being used by one of the patient's contact"
                  );
                  withoutErrors = false;
                }
              } else {
                this.errors.contact.phone.push("Invalid phone");
                withoutErrors = false;
              }
            }
          }
        }
        if (this.entity == 1) {
          if (this.otherName == null || this.otherName == "") {
            this.errors.other.name.push("Required Name field!");
            withoutErrors = false;
          }
          if (this.otherEmail) {
            if (!validateEmail(this.otherEmail)) {
              this.errors.other.email.push("Invalid email");
              withoutErrors = false;
            }
          }
          if (
            this.otherPhone != null &&
            this.otherPhone != "+1" &&
            !validatePhoneNumber(this.otherPhone)
          ) {
            this.errors.other.phone.push("Invalid phone number");
            withoutErrors = false;
          }
          if (this.otherAddress == null || this.otherAddress == "") {
            this.errors.other.address.push("Required Address field!");
            withoutErrors = false;
          }
          if (
            this.otherFax == null &&
            this.otherFax == "+1" &&
            !validatePhoneNumber(this.otherFax)
          ) {
            this.errors.other.fax.push("Invalid fax number");
            withoutErrors = false;
          }
        }
      } else {
        if (this.entity == 0) {
          if (this.contact.value == 0) {
            if (this.contactEmail) {
              if (this.isEmailContact(this.contactEmail)) {
                this.errors.contact.email.push(
                  "This email is being used by one of the patient's contact"
                );
                withoutErrors = false;
              }
            }
            if (this.contactPhone) {
              if (this.isPhoneContact(this.contactPhone)) {
                this.errors.contact.phone.push(
                  "This phone is being used by one of the patient's contact"
                );
                withoutErrors = false;
              }
            }
          }
        }
      }
      return withoutErrors;
    },
    async submitComponentData(documentId) {
      const result = this.validateReleaseOfInformationSubmit();
      if (result) {
        const formData = {
          DocumentId: documentId,
          Attention: this.attention ?? null,
          ToType: this.releaseTo ?? null,
          ToEntity: this.entity ?? null,
          ExpirationDate:
            this.expirationDate != null && this.expirationDate != ""
              ? this.formatDateTimeSql(this.expirationDate)
              : null,
          ToOrganizationName: this.otherName ?? null,
          ToOrganizationAttention: this.otherAttention ?? null,
          ToOrganizationEmail: this.otherEmail ?? null,
          ToOrganizationPhone: this.otherPhone ?? null,
          ToOrganizationAddress: this.otherAddress ?? null,
          ToOrganizationFax: this.otherFax ?? null
        };
        if (this.entity == 0 && this.contact.value == 0) {
          const newContactData = {
            Name: this.contactFullName ?? null,
            RelationshipId: this.contactRelationship?.value ?? null,
            Email: this.contactEmail ?? null,
            Phone: this.contactPhone ?? null,
            Address: this.contactStreet ?? null,
            ZipCode: this.contactZipcode ?? null,
            City: this.contactCity ?? null,
            StateId: this.contactState?.value ?? null,
            PatientId: this.patient.patientId
          };
          var newPatientContact =
            this.contactFullName != null
              ? await patientContactsServer.create(newContactData)
              : null;
          formData["ToPatientContactId"] = newPatientContact?.id;
        } else if (this.entity == 0) {
          formData["ToPatientContactId"] = this.contact.value;
        }
        const patientName = this.formatClient(
          this.currentUser.firstName ?? "",
          this.currentUser.middleName ?? "",
          "",
          this.currentUser.lastName ?? "",
          ""
        );
        if (this.formId) {
          await releaseOfInformationServer.update(this.formId, formData);
        } else {
          formData[
            "Creation"
          ] = `Created by ${patientName} \n on ${this.formatDate(new Date())}`;
          await releaseOfInformationServer.create(formData);
        }
        await this.fetchReleaseToContactOptions();
        await this.fecthReleaseOfInformation();
      }
    },
    getBakedLayout() {
      return `<div class="mt-2 mx-2" style="font-size:13px;">
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">1. Client Information</legend>
                  <div>
                    <p class="my-1">
                      <span class="font-weight-bold">Legal Name: </span
                      >${this.formatClient(
                        this.patient?.patient.firstName ?? "",
                        this.patient?.patient.middleName ?? "",
                        this.patient?.patient.preferredName ?? "",
                        this.patient?.patient.lastName ?? ""
                      )}
                    </p>
                    <p class="my-1">
                      <span class="font-weight-bold">DOB: </span
                      >${this.formatDateOnly(this.patient?.patient.dateOfBirth)}
                    </p>
                  </div>
              </fieldset>
            </div>
            <div class="col"></div>
        </div>
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">2. I give my permission to the clinic and professional(s) stated below</legend>
                  <div>
                    <p class="my-0">${this.systemSettings.organizationName ??
                      ""}</p>
                    <p class="my-0">
                      ${this.getOrganizationInfo ?? ""}
                    </p>
                  </div>
              </fieldset>
            </div>
            <div class="col"></div>
        </div>
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                <span class="font-weight-bold">Attention: </span>
                <div>
                  ${this.attention ?? ""}
                </div>
              </fieldset>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">
                    <div class="d-flex justify-content-between">
                        <div>3. To:</div>
                    </div>
                  </legend><div>
                    <p class="my-1 mb-2">
                      ${this.releaseToOptions.find(
                        x => x.value == this.releaseTo
                      )?.text ?? ""} ${this.entityOptions.find(
        x => x.value == this.entity
      )?.text ?? ""}
                    </p>
                    ${this.getContactInfo ?? ""}
                  </div>
              </fieldset>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col">
              <fieldset class="form-group" >
                  <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0" style="font-size: 15px; font-weight: bold;">
                    <div class="d-flex justify-content-between">
                        <div>Expiration Date:</div>
                    </div>
                  </legend><div>
                  ${this.formatDateOnly(this.expirationDate) ?? ""}
                  </div>
              </fieldset>
            </div>
        </div>
      </div>`;
    },
    resetErrors() {
      this.errors = {
        releaseTo: [],
        entity: [],
        expirationDate: [],
        contact: {
          name: [],
          phone: [],
          email: []
        },
        other: {
          name: [],
          phone: [],
          email: [],
          address: [],
          fax: []
        },
      };
    }
  },
  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    ...mapState("client", {
      relationships: state =>
        state.relationships.map(relationship => ({
          value: relationship.id,
          label: relationship.name
        })),
      states: state =>
        state.states.map(s => ({
          label: s.name,
          value: s.id,
          data: s
        }))
    }),
    getOrganizationInfo() {
      let organization = [];
      if (this.systemSettings.organizationAddressLine1 != null) {
        organization.push(this.systemSettings.organizationAddressLine1);
      }
      if (this.systemSettings.organizationAddressLine2 != null) {
        organization.push(this.systemSettings.organizationAddressLine2);
      }
      if (this.systemSettings.organizationState != null) {
        const state = this.states.find(
          x => x.value == this.systemSettings.organizationState
        );
        if (state != null) {
          organization.push(state.data.abbreviation);
        }
      }
      if (this.systemSettings.organizationZipcode != null) {
        organization.push(this.systemSettings.organizationZipcode);
      }
      return organization.join(", ");
    },
    getContactInfo() {
      return this.entity == 0
        ? `
            <p class="my-1">
              <span class="font-weight-bold">Full Name: </span>
                ${this.contactFullName ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Relationship: </span>
                ${this.contactRelationship?.label ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Email: </span>
                ${this.contactEmail ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Mobile Phone Number: </span>
                ${
                  this.contactPhone != null && this.contactPhone != "+1"
                    ? this.contactPhone
                    : ""
                }
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Street Address: </span>
                ${this.contactStreet ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">City: </span>
                ${this.contactCity ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Zip Code: </span>
                ${this.contactZipcode ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">State: </span>
                ${this.contactState?.label ?? ""}
            </p>
          `
        : `
            <p class="my-1">
              <span class="font-weight-bold">Organization/Agency Name: </span>
                ${this.otherName ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Attention: </span>
                ${this.otherAttention ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Email: </span>
                ${this.otherEmail ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Phone: </span>
                ${
                  this.otherPhone != null && this.otherPhone != "+1"
                    ? this.otherPhone
                    : ""
                }
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Address: </span>
                ${this.otherAddress ?? ""}
            </p>
            <p class="my-1">
              <span class="font-weight-bold">Fax: </span>
                ${
                  this.otherFax != null && this.otherFax != "+1"
                    ? this.otherFax
                    : ""
                }
            </p>
          `;
    }
  },
  watch: {
    contact: function(value) {
      if (value == null) this.contact = this.releaseToContactOptions[0];
    },
    "contact.value": function(value) {
      if (value == 0) {
        this.cleanContactData();
      }
    }
  }
};
</script>
